import { useLayoutEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useStores } from '../../../stores/useStores';
import type { EllipseCrownOrbitAnimationRef } from '../../EllipseOrbitAnimation/EllipseOrbitAnimation';
import { EllipseOrbitAnimation } from '../../EllipseOrbitAnimation/EllipseOrbitAnimation';
import { getSlideOrder, HOME_PAGE_ORDER } from '../../FullPageDialogs/config';
import type { HornsAnimationRef } from '../../HornsAnimation/HornsAnimation';
import { HornsAnimation } from '../../HornsAnimation/HornsAnimation';
import Crown from '../../SVGIcons/Crown';

import styles from './FooterNavigation.module.scss';
import { getFooterColors } from './getFooterColors';
import { LobbyTabLine } from './LobbyTabLine';
import { RegularTabLine } from './RegularTabLine';

const TABS_COUNT = 5;

const translateXForOrder = (order: number) => (TABS_COUNT + 1 - order) * -100 + '%';

let lastSlideOrder = HOME_PAGE_ORDER;

export const FooterAnimations = observer(function FooterAnimations() {
  const currentDialogOrder = useRef(lastSlideOrder);
  const shifterRef = useRef<HTMLDivElement | null>(null);
  const ellipseCrownOrbitRef = useRef<EllipseCrownOrbitAnimationRef>(null);
  const hornsAnimationRef = useRef<HornsAnimationRef>(null);

  const { uiStore, coinTypeStore, bingoStore } = useStores();

  const isOnBingoPage = bingoStore.isBingoPageOpen;

  const setIndicator = (order: number, animate: boolean) => {
    const shifter = shifterRef.current;
    if (!shifter) return;
    const isHome = order === HOME_PAGE_ORDER;
    if (!animate) {
      const prevTransition = shifter.style.transition;
      shifter.style.transition = 'none';
      shifter.style.setProperty('--orderTranslateX', translateXForOrder(order));
      void shifter.offsetWidth;
      shifter.style.transition = prevTransition;
    } else {
      shifter.style.setProperty('--orderTranslateX', translateXForOrder(order));
    }
    shifter.style.setProperty('--lobbyTabLineOpacity', isHome ? '1' : '0');
    shifter.style.setProperty('--regularTabLineOpacity', isHome ? '0' : '1');
  };

  useLayoutEffect(() => {
    const slideOrder = getSlideOrder(isOnBingoPage ? 'bingo' : uiStore.openFullPageDialog);
    const fromOrder = currentDialogOrder.current;

    if (fromOrder === slideOrder) {
      setIndicator(slideOrder, false);
      return;
    }

    setIndicator(fromOrder, false);
    setIndicator(slideOrder, true);

    void ellipseCrownOrbitRef.current?.start(slideOrder >= fromOrder ? 'right' : 'left');
    if (slideOrder === HOME_PAGE_ORDER) {
      void hornsAnimationRef.current?.start();
    } else {
      void hornsAnimationRef.current?.hide();
    }

    currentDialogOrder.current = slideOrder;
    lastSlideOrder = slideOrder;
  }, [uiStore.openFullPageDialog, isOnBingoPage]);

  const lineColor = useMemo(() => {
    const { mainColor } = getFooterColors(coinTypeStore.coinType);
    return mainColor;
  }, [coinTypeStore.coinType]);

  return (
    <div className={styles.animationsContainer}>
      <div ref={shifterRef} className={styles.footerNavigationShifter}>
        <div className={styles.footerNavigationShifterArrow}>
          <div className={styles.regularTabLine}>
            <EllipseOrbitAnimation
              ref={ellipseCrownOrbitRef}
              innerClassName={styles.ellipseCrownOrbitInner}
              wrapperClassName={styles.ellipseCrownOrbit}
            >
              <Crown />
            </EllipseOrbitAnimation>
            <RegularTabLine className={styles.tabLine} mainColor={lineColor} />
          </div>
          <div className={styles.lobbyTabLine}>
            <div className={styles.hornsAnimationWrapper}>
              <HornsAnimation ref={hornsAnimationRef} shown>
                <Crown />
              </HornsAnimation>
            </div>
            <LobbyTabLine className={classNames(styles.tabLine, styles.lobbyTabLine)} mainColor={lineColor} />
          </div>
        </div>
      </div>
    </div>
  );
});
